import PropTypes from "prop-types";
import React, { Component } from "react";
import {
  Modal,
  Button,
  Segment,
  Grid,
  Statistic,
  Label,
  Message,
  Icon,
  Loader as SemanticLoader,
} from "semantic-ui-react";

export class CheckoutModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      overrideActive: false,
      loading: false,
    };
  }

  getPatronRole = () => {
    const { patronData } = this.props;

    const patronType =
      patronData?.patron_type ||
      patronData?.metadata?.patron_type ||
      patronData?.user_type ||
      "PATRON";

    return patronType.toUpperCase();
  };

  hasPriorRequest = () => {
    const { patronData } = this.props;

    if (!patronData) return false;

    const metadata = patronData.metadata || {};

    return (
      metadata.hasRequestForThisItem ||
      metadata.hasRequestForOtherCopy ||
      metadata.hasRequest
    );
  };

  getRequestType = () => {
    const { patronData } = this.props;

    if (!patronData) return null;

    const metadata = patronData.metadata || {};

    if (metadata.hasRequestForThisItem) {
      return "REQUEST_THIS_COPY";
    }
    if (metadata.hasRequestForOtherCopy) {
      return "REQUEST_OTHER_COPY";
    }
    if (metadata.hasRequest) {
      return "HAS_REQUEST";
    }
    return null;
  };

  getPatronStatus = () => {
    const restrictions = this.getRestrictions();
    const canCheckout =
      !restrictions.hasOverdueItems && restrictions.isBorrowingLimitOk;

    return {
      canCheckout,
      label: canCheckout ? "Can Checkout" : "Restricted",
      color: canCheckout ? "green" : "red",
    };
  };

  getRestrictions = () => {
    const { borrowingStats, patronData } = this.props;
    const role = this.getPatronRole();

    const hasOverdueItems =
      patronData?.status === "restricted" || patronData?.hasOverdueLoans;
    const limitReached = borrowingStats && !borrowingStats.canBorrow;
    const isBorrowingLimitOk = borrowingStats && borrowingStats.canBorrow;
    const isFaculty = role === "FACULTY";
    const isStudent = role === "STUDENT";
    const isResearcher = role === "RESEARCHER";
    const isVisitor = role === "VISITOR";

    return {
      role,
      isFaculty,
      isStudent,
      isResearcher,
      isVisitor,
      hasOverdueItems,
      limitReached,
      isBorrowingLimitOk,
      canOverride: isFaculty || isResearcher,
      hasRestrictions: hasOverdueItems || limitReached,
    };
  };

  canCheckout = () => {
    const restrictions = this.getRestrictions();
    const { overrideActive } = this.state;

    if (restrictions.hasRestrictions && !overrideActive) {
      return false;
    }
    if (
      restrictions.hasRestrictions &&
      overrideActive &&
      !restrictions.canOverride
    ) {
      return false;
    }
    return true;
  };

  handleCheckout = () => {
    const { onCheckout, item } = this.props;
    const { overrideActive } = this.state;

    if (!this.canCheckout()) return;

    this.setState({ loading: true });
    setTimeout(() => {
      onCheckout(item, overrideActive);
      this.setState({ loading: false });
    }, 500);
  };

  render() {
    const { open, onClose, item, borrowingStats, loading } = this.props;
    const { overrideActive } = this.state;
    const restrictions = this.getRestrictions();
    const canCheckout = this.canCheckout();
    const hasPriorRequest = this.hasPriorRequest();
    const requestType = this.getRequestType();

    return (
      <Modal open={open} onClose={onClose} size="small" closeIcon>
        <Modal.Header>
          You are about to checkout the physical copy of{" "}
          <strong>{item?.title || item?.document?.title}</strong>.
        </Modal.Header>

        <Modal.Content>
          {/* Status */}
          <div style={{ marginBottom: "10px" }}>
            <strong>Patron Status:</strong>
            <Label
              color={this.getPatronStatus().color}
              style={{ marginLeft: "10px" }}
              size="small"
            >
              {this.getPatronStatus().label}
            </Label>
          </div>

          {/* Stats */}
          {loading ? (
            <Segment>
              <SemanticLoader active inline="centered" />
            </Segment>
          ) : (
            <Grid columns={3} stackable>
              <Grid.Row>
                <Grid.Column textAlign="center">
                  <Statistic size="tiny">
                    <Statistic.Value>{borrowingStats?.current}</Statistic.Value>
                    <Statistic.Label>Active Loans</Statistic.Label>
                  </Statistic>
                </Grid.Column>
                <Grid.Column textAlign="center">
                  <Statistic size="tiny">
                    <Statistic.Value>
                      {borrowingStats?.current}/{borrowingStats?.limit}
                    </Statistic.Value>
                    <Statistic.Label>Borrowing Limit</Statistic.Label>
                  </Statistic>
                </Grid.Column>
                <Grid.Column textAlign="center">
                  <Statistic size="tiny">
                    <Statistic.Value
                      color={
                        restrictions.isStudent
                          ? "orange"
                          : restrictions.isFaculty
                          ? "green"
                          : restrictions.isResearcher
                          ? "purple"
                          : "blue"
                      }
                    >
                      {restrictions.role}
                    </Statistic.Value>
                    <Statistic.Label>Role</Statistic.Label>
                  </Statistic>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          )}

          {/* Tags */}
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              gap: "6px",
              flexWrap: "wrap",
            }}
          >
            {restrictions.hasOverdueItems && (
              <Label color="red" size="small">
                <Icon name="exclamation circle" />
                Overdue Items
              </Label>
            )}
            {restrictions.limitReached && (
              <Label color="orange" size="small">
                <Icon name="warning circle" />
                Limit Reached
              </Label>
            )}

            {requestType === "REQUEST_THIS_COPY" && (
              <Label color="green" size="small">
                <Icon name="check circle" />
                Request for This Copy
              </Label>
            )}
            {requestType === "REQUEST_OTHER_COPY" && (
              <Label color="blue" size="small">
                <Icon name="info circle" />
                Has Request
              </Label>
            )}
            {!hasPriorRequest && (
              <Label color="grey" size="small">
                <Icon name="circle outline" />
                No Prior Request
              </Label>
            )}
          </div>

          {/* Restrictions Message */}
          {restrictions.hasRestrictions && !overrideActive && (
            <Message
              warning
              style={{ marginTop: "10px", padding: "10px", fontSize: "12px" }}
            >
              <Message.Header>Checkout Restricted</Message.Header>
              {restrictions.hasOverdueItems && (
                <p style={{ margin: "4px 0" }}>Patron has overdue items.</p>
              )}
              {restrictions.limitReached && (
                <p style={{ margin: "4px 0" }}>
                  Patron has reached borrowing limit ({borrowingStats?.current}/
                  {borrowingStats?.limit}).
                </p>
              )}
              {!restrictions.canOverride && (
                <p style={{ margin: "4px 0" }}>
                  Override is not available for {restrictions.role}s.
                </p>
              )}
            </Message>
          )}

          {/* Override Confirmation */}
          {overrideActive && restrictions.canOverride && (
            <Message
              warning
              icon
              style={{ marginTop: "10px", padding: "10px", fontSize: "12px" }}
            >
              <Icon name="warning circle" />
              <Message.Content>
                <Message.Header>Override Mode Active</Message.Header>
                This checkout will bypass restrictions and be logged.
              </Message.Content>
            </Message>
          )}
        </Modal.Content>

        <Modal.Actions>
          <Button onClick={onClose} size="small">
            Close
          </Button>

          {!canCheckout ? (
            <>
              <Button positive disabled size="small">
                Checkout Not Allowed
              </Button>
              {restrictions.canOverride && !overrideActive && (
                <Button
                  color="orange"
                  onClick={() => this.setState({ overrideActive: true })}
                  icon="shield alternate"
                  labelPosition="right"
                  content="Override"
                  size="small"
                />
              )}
              {overrideActive && restrictions.canOverride && (
                <Button
                  color="grey"
                  onClick={() => this.setState({ overrideActive: false })}
                  content="Cancel Override"
                  size="small"
                />
              )}
            </>
          ) : (
            <Button
              positive
              onClick={this.handleCheckout}
              loading={this.state.loading}
              disabled={this.state.loading}
              icon="check"
              labelPosition="right"
              content={`Checkout${overrideActive ? " (Override)" : ""}`}
              size="small"
            />
          )}
        </Modal.Actions>
      </Modal>
    );
  }
}

CheckoutModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCheckout: PropTypes.func.isRequired,
  item: PropTypes.object,
  patronData: PropTypes.object,
  borrowingStats: PropTypes.object,
  loading: PropTypes.bool,
};

CheckoutModal.defaultProps = {
  item: null,
  patronData: null,
  borrowingStats: null,
  loading: false,
};
